import * as React from 'react'
import { Loader2, X } from 'lucide-react'
import {
  abortIngestUpload,
  selectUploadProgress,
  useAppSelector,
  useAbortDocumentAdditionMutation,
  useBeginDocumentAdditionMutation,
  useCompleteDocumentAdditionMutation,
  useUploadUnstructuredCorpusMutation,
  type BeginDocumentAdditionResult,
  type CompleteDocumentAdditionResult
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { cn } from '../../lib/utils'

const ADDITIONAL_CONTEXT_MAX_CHARS = 4000

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Pull a human-readable message out of RTK Query / XHR upload failures. */
function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  if (typeof err === 'object' && err !== null) {
    const obj = err as Record<string, unknown>
    // RTK / XHR: { status, data } — data may be JSON, plain text, or null.
    if (typeof obj.data === 'string' && obj.data.trim()) return obj.data
    if (typeof obj.data === 'object' && obj.data !== null) {
      const data = obj.data as Record<string, unknown>
      // api-service shape: { error: string }; proxy AIP-193: { message, reason }.
      if (typeof data.error === 'string' && data.error.trim()) return data.error
      if (typeof data.message === 'string' && data.message.trim()) return data.message
      if (typeof data.reason === 'string' && data.reason.trim()) return data.reason
    }
    if (typeof obj.error === 'string' && obj.error.trim()) return obj.error
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message
    // Never fall through to String(obj) → "[object Object]".
    try {
      return JSON.stringify(err)
    } catch {
      return 'Upload failed'
    }
  }
  return String(err)
}

export interface AddDocumentsProps {
  /** Existing job id for aria labels; omit for a brand-new ingest. */
  jobId?: string
  /** When set, begin reuses this job (append/reopen). Omit for a fresh job. */
  contextJobId?: string
  /** Files staged by the parent (e.g. card drop) before/while the modal opens. */
  seedFiles?: File[] | null
  /** Cleared after `seedFiles` are absorbed into local staged state. */
  onSeedConsumed?: () => void
  /** Expose transfer-open state for drop handlers / parent orchestration. */
  onTransferOpenChange?: (open: boolean) => void
  /** Called after a successful complete (e.g. close the modal / open the job). */
  onComplete?: (result: CompleteDocumentAdditionResult) => void
  className?: string
}

/**
 * Document upload control: stage chips, select files, upload with
 * transfer-scoped progress/cancel/retry. Used for Add documents and fresh ingest.
 */
export function AddDocuments({
  jobId,
  contextJobId,
  seedFiles,
  onSeedConsumed,
  onTransferOpenChange,
  onComplete,
  className
}: AddDocumentsProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [staged, setStaged] = React.useState<File[]>([])
  const [isDragActive, setIsDragActive] = React.useState(false)
  const [additionalContext, setAdditionalContext] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [completionNotices, setCompletionNotices] = React.useState<string[]>([])
  const [completionResult, setCompletionResult] =
    React.useState<CompleteDocumentAdditionResult | null>(null)
  const [transfer, setTransfer] = React.useState<BeginDocumentAdditionResult | null>(null)
  const [busy, setBusy] = React.useState(false)

  const [beginAddition] = useBeginDocumentAdditionMutation()
  const [uploadCorpus] = useUploadUnstructuredCorpusMutation()
  const [completeAddition] = useCompleteDocumentAdditionMutation()
  const [abortAddition] = useAbortDocumentAdditionMutation()

  const progress = useAppSelector((state) =>
    selectUploadProgress(state, transfer?.uploadId ?? undefined)
  )
  const transferOpen = busy
  const stagingLocked = busy || transfer != null

  const stageFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0 || stagingLocked) return
      setStaged((prev) => {
        const keys = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`))
        const next = [...prev]
        for (const file of files) {
          const key = `${file.name}|${file.size}|${file.lastModified}`
          if (!keys.has(key)) {
            keys.add(key)
            next.push(file)
          }
        }
        return next
      })
      setError(null)
    },
    [stagingLocked]
  )

  React.useEffect(() => {
    if (!seedFiles || seedFiles.length === 0) return
    stageFiles(seedFiles)
    onSeedConsumed?.()
  }, [seedFiles, stageFiles, onSeedConsumed])

  React.useEffect(() => {
    onTransferOpenChange?.(transferOpen)
  }, [onTransferOpenChange, transferOpen])

  const removeStaged = (index: number): void => {
    if (stagingLocked) return
    setStaged((prev) => prev.filter((_, i) => i !== index))
  }

  const cancelTransfer = (): void => {
    if (transfer) {
      abortIngestUpload(transfer.uploadId)
      void abortAddition({ jobId: transfer.jobId, uploadId: transfer.uploadId })
    }
    setBusy(false)
    setError('Upload cancelled.')
  }

  const runUpload = async (): Promise<void> => {
    if (staged.length === 0 || busy) return
    setError(null)
    setBusy(true)

    const fileName =
      staged.length === 1 ? staged[0].name : `${staged[0].name} + ${staged.length - 1} more`

    let activeTransfer = transfer
    try {
      const begin = await beginAddition(
        activeTransfer
          ? {
              fileName,
              contextJobId: activeTransfer.jobId,
              uploadId: activeTransfer.uploadId
            }
          : {
              fileName,
              ...(contextJobId ? { contextJobId } : {})
            }
      ).unwrap()
      activeTransfer = begin
      setTransfer(begin)

      const unzip = await uploadCorpus({
        jobId: begin.jobId,
        files: staged,
        uploadId: begin.uploadId
      }).unwrap()

      const trimmedContext = additionalContext.trim()
      const result = await completeAddition({
        jobId: begin.jobId,
        uploadId: begin.uploadId,
        unzip,
        fileName,
        ...(trimmedContext ? { additionalContext: trimmedContext } : {})
      }).unwrap()

      const notices = [
        ...(unzip.pathRenames?.map(({ from, to }) => `${from} → ${to}`) ?? []),
        ...(unzip.idempotentSkips?.map((path) => `${path} was already present`) ?? [])
      ]
      setStaged([])
      setTransfer(null)
      setBusy(false)
      if (notices.length > 0) {
        setCompletionResult(result)
        setCompletionNotices(notices)
      } else {
        onComplete?.(result)
      }
    } catch (err) {
      if (activeTransfer) {
        abortIngestUpload(activeTransfer.uploadId)
        void abortAddition({
          jobId: activeTransfer.jobId,
          uploadId: activeTransfer.uploadId
        })
      }
      setBusy(false)
      setError(extractErrorMessage(err))
    }
  }

  if (completionNotices.length > 0) {
    return (
      <div className={cn('flex flex-col gap-3', className)} role="status">
        <p className="m-0 text-sm font-medium text-foreground">Documents added</p>
        <ul className="m-0 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          {completionNotices.map((notice) => (
            <li key={notice}>{notice}</li>
          ))}
        </ul>
        <Button
          type="button"
          size="xs"
          className="self-end"
          onClick={() => {
            const result = completionResult
            setCompletionNotices([])
            setCompletionResult(null)
            if (result) onComplete?.(result)
          }}
        >
          Done
        </Button>
      </div>
    )
  }

  const dropState = staged.length > 0 || stagingLocked ? 'staged' : isDragActive ? 'drag' : 'idle'
  // Fresh ingest (no context job) uses Ingest copy; append/reopen keeps Upload.
  const isFreshIngest = !contextJobId
  const submitIdleLabel =
    staged.length === 1
      ? isFreshIngest
        ? 'Ingest'
        : 'Upload'
      : isFreshIngest
        ? `Ingest ${staged.length} files`
        : `Upload ${staged.length} files`
  const submitBusyLabel = isFreshIngest ? 'Ingesting…' : 'Uploading…'

  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      <div
        data-state={dropState}
        onDragOver={(event) => {
          if (!event.dataTransfer.types.includes('Files')) return
          event.preventDefault()
          event.dataTransfer.dropEffect = stagingLocked ? 'none' : 'copy'
          if (!stagingLocked) setIsDragActive(true)
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={(event) => {
          if (!event.dataTransfer.types.includes('Files')) return
          event.preventDefault()
          setIsDragActive(false)
          if (!stagingLocked) stageFiles(Array.from(event.dataTransfer.files ?? []))
        }}
        className={cn(
          'flex min-w-0 flex-col gap-3 rounded-md border-2 border-dashed border-brand bg-brand-soft p-3 transition-colors',
          'data-[state=drag]:border-brand data-[state=drag]:bg-brand-soft/80'
        )}
      >
        <p className="m-0 text-xs text-muted-foreground">Drop files here or browse.</p>

        {staged.length > 0 ? (
          <div className="flex w-full min-w-0 flex-col gap-1.5">
            {staged.map((file, index) => (
              <span
                key={`${file.name}-${file.lastModified}-${index}`}
                className="flex min-w-0 max-w-full items-center gap-1 overflow-hidden rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-11 text-foreground"
                title={file.name}
              >
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="shrink-0 text-muted-foreground">{formatBytes(file.size)}</span>
                {!stagingLocked ? (
                  <button
                    type="button"
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeStaged(index)}
                  >
                    <X className="size-3" />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          disabled={stagingLocked}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? [])
            event.target.value = ''
            stageFiles(files)
          }}
        />

        <Button
          type="button"
          size="xs"
          variant="outline"
          className="self-start"
          disabled={stagingLocked}
          aria-label={jobId ? `Select files for job ${jobId}` : 'Select files'}
          onClick={() => inputRef.current?.click()}
        >
          Select files
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="ingest-additional-context" className="text-xs text-muted-foreground">
          Additional context (optional)
        </Label>
        <Textarea
          id="ingest-additional-context"
          value={additionalContext}
          maxLength={ADDITIONAL_CONTEXT_MAX_CHARS}
          disabled={busy}
          placeholder="Anything the ingest agent should know about these files — site, line, naming conventions, what to skip…"
          className="max-h-40 min-h-20 overflow-y-auto text-sm"
          onChange={(event) => setAdditionalContext(event.target.value)}
        />
      </div>

      {progress ? (
        <div className="flex items-center gap-2 text-11 text-muted-foreground">
          <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-brand transition-[width]"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <span className="shrink-0 tabular-nums">{progress.pct}%</span>
          <Button type="button" size="xs" variant="ghost" onClick={cancelTransfer}>
            Cancel
          </Button>
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="text-11 text-destructive">
          {error}{' '}
          {staged.length > 0 && !busy ? (
            <button
              type="button"
              className="font-medium underline-offset-2 hover:underline"
              onClick={() => void runUpload()}
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      {staged.length > 0 ? (
        <Button
          type="button"
          size="xs"
          variant="outline"
          className="self-end border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/70 hover:text-brand"
          disabled={busy}
          onClick={() => void runUpload()}
        >
          {busy ? <Loader2 className="size-3 animate-spin" /> : null}
          {busy ? submitBusyLabel : submitIdleLabel}
        </Button>
      ) : null}
    </div>
  )
}
